import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageHeader } from "@/components/FilterBar";
import { Panel } from "@/components/KpiCard";
import { PeriodPicker, periodeParams } from "@/components/PeriodPicker";
import { RapportMoisDernier } from "@/components/RapportMoisDernier";
import { useFilters } from "@/context/FilterContext";
import { api, errMsg } from "@/lib/api";

const DOCS = [
  ["rapprochement", "Rapport de rapprochement PDF", "avec les lettres de contestation en annexe"],
  ["commentaires_pdf", "Commentaires sur opérations (PDF)", "journal comptable de la période"],
  ["commentaires_xlsx", "Export Excel du journal", "fichier modifiable pour le comptable"],
  ["interets", "Intérêts hypothécaires (PDF)", "total annuel par immeuble : intérêts déductibles et capital remboursé"],
];

const DocChoix = ({ docs, setDocs }) => (
  <div className="space-y-3" data-testid="envoi-documents">
    {DOCS.map(([k, l, d]) => (
      <label key={k} className="flex items-start gap-3 cursor-pointer">
        <Checkbox checked={docs.includes(k)} onCheckedChange={(v) => setDocs(v ? [...docs, k] : docs.filter((x) => x !== k))} data-testid={`envoi-doc-${k}`} className="mt-0.5" />
        <span><span className="text-sm font-medium text-slate-900">{l}</span><span className="block text-xs text-slate-500">{d}</span></span>
      </label>
    ))}
  </div>
);

const Historique = ({ envois }) => (
  <Panel title="Derniers envois" testId="envois-historique">
    {!envois.length ? <p className="text-sm text-slate-500">Aucun envoi pour l'instant.</p> : (
      <ul className="-my-2 divide-y divide-slate-100">
        {envois.map((e) => (
          <li key={e.id} className="py-3 text-sm" data-testid={`envoi-${e.id}`}>
            <p className="font-medium text-slate-900">Du {e.debut} au {e.fin} → {e.destinataire}</p>
            <p className="text-xs text-slate-500">{new Date(e.created_at).toLocaleString("fr-CA")} · {e.fichiers.length} pièce(s) jointe(s){e.copie ? " · copie reçue" : ""}</p>
          </li>
        ))}
      </ul>
    )}
  </Panel>
);

export default function EnvoiComptable() {
  const { options } = useFilters();
  const [periode, setPeriode] = useState(null);
  const [cpt, setCpt] = useState(null);
  const [message, setMessage] = useState("");
  const [docs, setDocs] = useState(DOCS.map(([k]) => k));
  const [envois, setEnvois] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = periodeParams(periode);

  const loadEnvois = useCallback(() => api.get("/comptable/envois").then((r) => setEnvois(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/comptable").then((r) => { setCpt(r.data); setMessage(r.data.message); }).catch((e) => toast.error(errMsg(e)));
    api.get("/rapprochement/periode").then((r) => setPeriode({ mode: r.data.fin ? r.data.fin.slice(0, 7) : "perso" })).catch(() => setPeriode({ mode: "perso" }));
    loadEnvois();
  }, [loadEnvois]);

  const envoyer = async () => {
    setConfirm(false); setBusy(true);
    try {
      const { data } = await api.post("/comptable/envoyer", { ...p, documents: docs, message });
      toast.success(`Courriel envoyé à ${data.destinataire}${data.copie ? " (copie envoyée à votre adresse)" : ""}`);
      loadEnvois();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!cpt || !periode) return <Loader2 className="h-6 w-6 animate-spin text-emerald-700" />;
  const pret = cpt.email && p.debut && docs.length && !busy;

  return (
    <div data-testid="envoi-comptable-page">
      <PageHeader eyebrow="Partage" title="Envoi au comptable"><RapportMoisDernier onEnvoye={loadEnvois} /></PageHeader>
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Panel title="Préparer l'envoi" className="xl:col-span-3" testId="envoi-panel">
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-sm" data-testid="envoi-destinataire">
              <Mail className="h-4 w-4 text-emerald-700" />
              {cpt.email ? <span>À : <strong>{cpt.nom ? `${cpt.nom} <${cpt.email}>` : cpt.email}</strong> · copie à votre adresse</span>
                : <span className="text-red-600">Aucune adresse enregistrée. <Link to="/parametres" className="underline" data-testid="envoi-lien-parametres">Ajoutez-la dans Paramètres</Link>.</span>}
            </div>
            <div className="space-y-2"><Label>Période</Label><PeriodPicker value={periode} onChange={setPeriode} mois={options.mois} sansTout /></div>
            <div className="space-y-2"><Label>Documents à joindre</Label><DocChoix docs={docs} setDocs={setDocs} /></div>
            <div className="space-y-2"><Label htmlFor="envoi-msg">Message</Label>
              <Textarea id="envoi-msg" rows={7} maxLength={3000} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="envoi-message-input" /></div>
            <Button onClick={() => setConfirm(true)} disabled={!pret} className="bg-emerald-700 hover:bg-emerald-800" data-testid="envoi-submit-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}{busy ? "Préparation et envoi…" : "Envoyer au comptable"}
            </Button>
          </div>
        </Panel>
        <div className="xl:col-span-2"><Historique envois={envois} /></div>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="envoi-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Envoyer {docs.length} document(s) ?</AlertDialogTitle>
            <AlertDialogDescription>Un vrai courriel sera envoyé à {cpt.email} pour la période du {p.debut} au {p.fin}, avec une copie à votre adresse.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="envoi-confirm-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={envoyer} className="bg-emerald-700 hover:bg-emerald-800" data-testid="envoi-confirm-button">Envoyer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
